import useAddReturnWorkflow from "@babackapp/private-api-client/client/use-add-return-workflow";
import useBulkDeleteReturnWorkflows from "@babackapp/private-api-client/client/use-bulk-delete-return-workflows";
import useBulkUpdateReturnWorkflows from "@babackapp/private-api-client/client/use-bulk-update-return-workflows";
import useDeleteReturnWorkflow from "@babackapp/private-api-client/client/use-delete-return-workflow";
import useGetAllReturnWorkflows, {
  invalidateAllReturnWorkflowsKey,
  useGetAllReturnWorkflowsSuspense,
} from "@babackapp/private-api-client/client/use-get-all-return-workflows";
import useGetAllReturnWorkflowExecutions from "@babackapp/private-api-client/client/use-get-all-return-workflow-executions";
import useUpdateReturnWorkflow from "@babackapp/private-api-client/client/use-update-return-workflow";
import {
  getVariablesForReturnTrigger,
  RETURN_VARIABLE_REGISTRY,
  type ReturnWorkflowTriggerType,
} from "@babackapp/return-variables-registry";
import { useMemo, type ReactNode } from "react";
import {
  WorkflowsEditorConfigProvider,
  type WorkflowsApiAdapter,
  type WorkflowsEditorConfig,
} from "./context/workflows-editor-config-context";
import {
  WorkflowsEditorRoutingProvider,
  type WorkflowsEditorRouting,
} from "./context/workflows-editor-routing-context";
import { useReturnEditorVariables } from "./lib/return-variables";
import { returnConditionFields } from "./nodes/condition/return-condition-config";
import { RETURN_TRIGGER_TYPE_OPTIONS } from "./nodes/trigger/constants";
import type { EditorNodeType } from "./nodes";

/**
 * Return API adapter: the dedicated return-workflows endpoints.
 *
 * The adapter slots are typed `typeof` the V2 hooks (see
 * workflows-editor-config-context.tsx). The return hooks are cast per slot:
 * both engines share the same graph JSON contract, and the return DTOs only
 * drop V2's `kind`/`origin` fields and rename the node DTO classes — the
 * shapes the editor core reads are identical. Decision recorded on BAB-2854
 * (option 1: adapter-level casts; generifying the core was not worth the
 * refactor).
 */
const RETURN_WORKFLOWS_API: WorkflowsApiAdapter = {
  useWorkflows:
    useGetAllReturnWorkflows as unknown as WorkflowsApiAdapter["useWorkflows"],
  useWorkflowsSuspense:
    useGetAllReturnWorkflowsSuspense as unknown as WorkflowsApiAdapter["useWorkflowsSuspense"],
  useAddWorkflow:
    useAddReturnWorkflow as unknown as WorkflowsApiAdapter["useAddWorkflow"],
  useUpdateWorkflow:
    useUpdateReturnWorkflow as unknown as WorkflowsApiAdapter["useUpdateWorkflow"],
  useDeleteWorkflow:
    useDeleteReturnWorkflow as unknown as WorkflowsApiAdapter["useDeleteWorkflow"],
  useBulkUpdateWorkflows:
    useBulkUpdateReturnWorkflows as unknown as WorkflowsApiAdapter["useBulkUpdateWorkflows"],
  useBulkDeleteWorkflows:
    useBulkDeleteReturnWorkflows as unknown as WorkflowsApiAdapter["useBulkDeleteWorkflows"],
  useWorkflowExecutions:
    useGetAllReturnWorkflowExecutions as unknown as WorkflowsApiAdapter["useWorkflowExecutions"],
  getWorkflowsQueryKey: invalidateAllReturnWorkflowsKey,
};

/**
 * Full return variable universe: the unavailable-set is computed against
 * every id of the domain registry (see WorkflowsEditorConfig.variableUniverse).
 */
const RETURN_VARIABLE_UNIVERSE: readonly string[] = Object.keys(
  RETURN_VARIABLE_REGISTRY,
);

/**
 * eventData fields the return engine records on executions
 * (ReturnWorkflowExecutionEventDataDto — shopId left out as noise).
 */
const RETURN_EXECUTION_EVENT_FIELDS = [
  {
    key: "returnRequestId",
    labelKey: "workflowsEditor.executions.fields.returnRequestId",
    defaultLabel: "Return",
  },
  {
    key: "orderId",
    labelKey: "workflowsEditor.executions.fields.orderId",
    defaultLabel: "Order",
  },
] as const;

/**
 * Return actions: email (BAB-2845/2852) + the V1 long-tail parity set
 * (BAB-2873): Shopify order note/attribute and merchant auto-treatment.
 */
const RETURN_ACTION_TYPES: readonly EditorNodeType[] = [
  "action:send_return_email",
  "action:set_order_note",
  "action:set_order_attribute",
  "action:auto_treat_return",
  // Shared nodes with tracking (BAB-2870/2871/2872) — same config contracts,
  // return-specific handlers (Shopify tags for modify_order_tags).
  "action:create_ticket_on_gorgias",
  "action:create_ticket_on_zendesk",
  "action:modify_order_tags",
];

export type ReturnWorkflowsProviderProps = {
  /** Current shop id (provided by the host app's router). */
  shopId: string;
  /** Navigate to a return template's editor page (host-provided). */
  openTemplate?: (templateId: string) => void;
  /**
   * Register a navigation guard for unsaved changes (host-provided).
   * Receives a callback returning the confirmation message; returns an
   * unsubscribe function.
   */
  registerNavigationGuard?: WorkflowsEditorRouting["registerNavigationGuard"];
  children: ReactNode;
};

/**
 * Return host context (baback-office): supplies the routing surface (shopId +
 * navigation, from props) and the return domain config (triggers, email
 * action, condition fields, variables). Router-agnostic by design — the host
 * passes shopId/navigation as props so the package never imports a router for
 * the return path. Mirror of templates-editor/src/return-provider.tsx.
 */
export function ReturnWorkflowsProvider({
  shopId,
  openTemplate,
  registerNavigationGuard,
  children,
}: ReturnWorkflowsProviderProps) {
  const routing = useMemo<WorkflowsEditorRouting>(
    () => ({ shopId, openTemplate, registerNavigationGuard }),
    [shopId, openTemplate, registerNavigationGuard],
  );

  const config = useMemo<WorkflowsEditorConfig>(
    () => ({
      domain: "return",
      api: RETURN_WORKFLOWS_API,
      availableTriggerTypes: RETURN_TRIGGER_TYPE_OPTIONS,
      availableActionTypes: RETURN_ACTION_TYPES,
      useVariables: useReturnEditorVariables,
      getVariablesForTrigger: (triggerType) =>
        getVariablesForReturnTrigger(triggerType as ReturnWorkflowTriggerType),
      variableUniverse: RETURN_VARIABLE_UNIVERSE,
      conditionFields: returnConditionFields,
      executionEventFields: RETURN_EXECUTION_EVENT_FIELDS,
      supportsZendeskCustomFields: true,
      // No tracking-specific create fields (kind/origin) on the return engine.
      createDefaults: {},
    }),
    [],
  );

  return (
    <WorkflowsEditorRoutingProvider value={routing}>
      <WorkflowsEditorConfigProvider config={config}>
        {children}
      </WorkflowsEditorConfigProvider>
    </WorkflowsEditorRoutingProvider>
  );
}
